import { useQueries } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import useAxiosPrivate from "./useAxiosPrivate";
import CategoryTrackMappingAPIs from "../apiRoutes/CategoryTrackMapping";
import { GET_CTM_OPTIONS_QUERY_KEY } from "../utils/constants/queryKeys";
import useAmsConfig from "./useAmsConfig";

const EMPTY_OPTS = { categories: [], tracks_by_category: {} };
const scopeKey = (app, language) => `${app}|${language}`;

// CTM options for every app x language in config; bulk grids pick both per row.
export default function useCtmOptionsByApp({ enabled = true } = {}) {
  const axiosPrivate = useAxiosPrivate();
  const { appNames, appLanguages } = useAmsConfig();
  const scopes = useMemo(
    () => appNames.flatMap((a) => (appLanguages[a.value] ?? []).map((l) => ({ app: a.value, language: l.value }))),
    [appNames, appLanguages]
  );

  const results = useQueries({
    queries: scopes.map(({ app, language }) => ({
      queryKey: [...GET_CTM_OPTIONS_QUERY_KEY, app, language],
      queryFn: () => CategoryTrackMappingAPIs.options({ axiosPrivate, app_name: app, language }),
      enabled: !!enabled,
    })),
  });

  const optionsByScope = useMemo(() => {
    const map = {};
    scopes.forEach(({ app, language }, i) => {
      map[scopeKey(app, language)] = results[i]?.data ?? EMPTY_OPTS;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopes.map((s) => scopeKey(s.app, s.language)).join("|"), results.map((r) => r?.data).join("|")]);

  const getCategories = useCallback(
    (app, language) => (optionsByScope[scopeKey(app, language)]?.categories ?? []).filter((c) => c && c.trim()),
    [optionsByScope]
  );

  const getTracks = useCallback(
    (app, language, category) => {
      if (!app || !language || !category) return [];
      const byCat = optionsByScope[scopeKey(app, language)]?.tracks_by_category ?? {};
      return (byCat[category] ?? []).filter((t) => t && t.track && t.track.trim());
    },
    [optionsByScope]
  );

  const getTrackNumber = useCallback(
    (app, language, category, track) => {
      if (!app || !language || !category || !track) return "";
      const byCat = optionsByScope[scopeKey(app, language)]?.tracks_by_category ?? {};
      const match = (byCat[category] ?? []).find((t) => t.track === track);
      return match && match.track_number != null ? String(match.track_number) : "";
    },
    [optionsByScope]
  );

  return { optionsByScope, getCategories, getTracks, getTrackNumber };
}
